import { useRoundLive } from "../../hooks/useRoundLive";
import { normalizePresentationStatus, PresentationStatus } from "../../interfaces/IPresentationStatus";
import { RoundLivePresentation } from "../../interfaces/IRoundsLive";
import { getPresentationBaseRowStyles, getPresentationRowStyles } from "../../utils/presentationStyles";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useEffect, useMemo, useRef } from "react";

const getHeaderCellStyles = (theme: Theme) => ({
  fontWeight: 600,
  px: 1.5,
  borderBottom: 1,
  borderColor: theme.palette.mode === "dark" ? "grey.600" : "divider",
});

interface PresentationReadOnlyRowProps {
  presentation: RoundLivePresentation;
}

function PresentationReadOnlyRow({ presentation }: PresentationReadOnlyRowProps) {
  const theme = useTheme();
  const isMediumOrLarger = useMediaQuery(theme.breakpoints.up("md"));
  const currentStatus = normalizePresentationStatus(presentation.status);
  const rowStyles = getPresentationRowStyles(currentStatus, theme);
  const baseStyles = getPresentationBaseRowStyles(theme);
  const startNo =
    presentation.startNo !== undefined && presentation.startNo !== null && presentation.startNo !== ""
      ? presentation.startNo
      : "—";

  return (
    <TableRow
      sx={{ ...rowStyles, ...baseStyles }}
      data-presentation-id={presentation.id}
      data-presentation-status={currentStatus}
    >
      <TableCell sx={{ width: "auto", borderBottom: "none" }}>
        <Typography variant="body2" fontWeight={500}>
          {presentation.name || "Prezentacja"}
        </Typography>
        {presentation.title ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mt: 0.25 }}>
            {presentation.title}
          </Typography>
        ) : null}
      </TableCell>
      {isMediumOrLarger ? (
        <TableCell sx={{ width: "auto", borderBottom: "none" }}>
          <Typography variant="body2" color="text.secondary">
            {presentation.organizationName || "—"}
          </Typography>
        </TableCell>
      ) : null}
      <TableCell align="center" sx={{ width: "80px", borderBottom: "none" }}>
        <Typography variant="body2" fontWeight={500}>
          {startNo}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

interface RoundPresentationsReadOnlyListProps {
  competitionId: string;
  categoryId: string;
  roundId: string;
  enabled: boolean;
  /** Indeks zakładki — do scrolla i `id` panelu */
  tabIndex: number;
}

/**
 * Read-only lista prezentacji rundy (bez kolumny Akcje), ze stylami jak host-panel.
 */
export default function RoundPresentationsReadOnlyList({
  competitionId,
  categoryId,
  roundId,
  enabled,
  tabIndex,
}: RoundPresentationsReadOnlyListProps) {
  const theme = useTheme();
  const isMediumOrLarger = useMediaQuery(theme.breakpoints.up("md"));
  const headerCellStyles = getHeaderCellStyles(theme);
  const { round, loading, error } = useRoundLive(competitionId, categoryId, roundId, enabled);

  const presentations = round?.presentations ?? [];

  const lastDancingId = useMemo(() => {
    let id: string | null = null;
    for (const p of presentations) {
      if (normalizePresentationStatus(p.status) === PresentationStatus.DANCING) {
        id = p.id;
      }
    }
    return id;
  }, [presentations]);

  const lastScrollKeyRef = useRef<string>("");

  useEffect(() => {
    if (!enabled || !lastDancingId) {
      if (!lastDancingId) {
        lastScrollKeyRef.current = "";
      }
      return;
    }

    const key = `${tabIndex}-${lastDancingId}`;
    if (key === lastScrollKeyRef.current) {
      return;
    }
    lastScrollKeyRef.current = key;

    const timeoutId = window.setTimeout(() => {
      const panel = document.getElementById(`round-tabpanel-${tabIndex}`);
      if (!panel || panel.hasAttribute("hidden")) {
        return;
      }

      const row = panel.querySelector<HTMLElement>(`tr[data-presentation-id="${lastDancingId}"]`);
      if (!row) {
        return;
      }

      const tabsAnchor = document.getElementById("schedule-position-tabs-anchor");
      const anchorHeight = tabsAnchor?.getBoundingClientRect().height ?? 0;
      /** Przy jednej rundzie kotwica ma wysokość 0 — wtedy offset jak przy pasku aplikacji */
      const offset = (anchorHeight > 0 ? anchorHeight : 56) + 8;
      const rect = row.getBoundingClientRect();
      window.scrollTo({
        top: Math.max(0, window.scrollY + rect.top - offset),
        behavior: "smooth",
      });
    }, 280);

    return () => window.clearTimeout(timeoutId);
  }, [enabled, lastDancingId, tabIndex, presentations.length]);

  const renderHeader = () => (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderBottom: 1,
        borderColor: theme.palette.mode === "dark" ? "grey.700" : "divider",
      }}
    >
      <Typography variant="subtitle1" fontWeight={600}>
        Prezentacje
      </Typography>
    </Box>
  );

  if (!enabled) {
    return null;
  }

  if (loading && presentations.length === 0) {
    return (
      <TableContainer component={Paper} sx={{ mt: 1 }}>
        {renderHeader()}
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Ładowanie prezentacji…
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  if (error) {
    return (
      <TableContainer component={Paper} sx={{ mt: 1 }}>
        {renderHeader()}
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  if (presentations.length === 0) {
    return (
      <TableContainer component={Paper} sx={{ mt: 1 }}>
        {renderHeader()}
        <Box sx={{ p: 2, pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Brak prezentacji w tej rundzie.
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 1 }}>
      {renderHeader()}
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellStyles}>Nazwa prezentacji</TableCell>
            {isMediumOrLarger ? <TableCell sx={headerCellStyles}>Nazwa organizacji</TableCell> : null}
            <TableCell align="center" sx={{ ...headerCellStyles, width: "80px" }}>
              Nr startowy
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {presentations.map((presentation, index) => (
            <PresentationReadOnlyRow key={`${roundId}-${presentation.id}-${index}`} presentation={presentation} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
